/**
 * Diagnostics mode types.
 *
 * DiagnosticsSession is the authoritative Firestore document shape stored in
 * the `sensei-diagnostics` database, /diagnostics/{sessionId}.
 * It matches the React version exactly so existing data is compatible.
 */

export type { DiagnosticTemplate } from "./data";

// ─── Question / Answer ────────────────────────────────────────────────────────

export interface QuestionOption {
  value: number;
  label: string;
}

/** A single diagnostic question — stored inside DiagnosticsSession.questions */
export interface DiagnosticQuestion {
  id:       string;
  text:     string;
  category?: string;
  options:  QuestionOption[];
}

/** Category grouping derived from questions array */
export interface DiagnosticCategory {
  name:          string;
  questionCount: number;
  startIndex:    number;
  endIndex:      number;
}

// ─── Collaborator ─────────────────────────────────────────────────────────────

export interface DiagnosticCollaborator {
  email: string;
  uid:   string | null;
}

// ─── Session (Firestore document) ─────────────────────────────────────────────

/**
 * Firestore document stored in sensei-diagnostics /diagnostics/{id}.
 * Must stay in sync with firestore.sensei-diagnostics.rules and the React version.
 */
export interface DiagnosticsSession {
  id:               string;
  title:            string;
  diagnosticId:     string;
  diagnosticTitle:  string;
  questions:        DiagnosticQuestion[];
  answers:          Record<string, number>;
  answer_authors:   Record<string, string>;
  analysis:         unknown | null;
  analysisMarkdown?: string;
  score:            number;
  completedAt:      string;
  isStarred:        boolean;
  isArchived:       boolean;
  isCompleted:      boolean;
  currentQuestion:  number;
  createdAt:        string;
  /** Required by Firestore rules */
  created_by_uid:   string;
  shared_with:      DiagnosticCollaborator[];
  invited_emails:   string[];
  resolved_uids:    string[];
  /** Public share token — when set, allows unauthenticated read via /diagnostics/shared/[token] */
  share_token?:     string;
  /** Client-side only — not stored in Firestore */
  role?:            "owner" | "collaborator";
}

// ─── Legacy result types (used by RadarChart, DomainScoreCard, GapTable, etc.) ─

export type MaturityLevel = 1 | 2 | 3 | 4 | 5;

export const MATURITY_LABELS: Record<MaturityLevel, string> = {
  1: "Initial",
  2: "Developing",
  3: "Defined",
  4: "Managed",
  5: "Optimising",
};

export interface DomainScore {
  domain:        string;
  score:         MaturityLevel;
  maxScore:      number;
  label:         string;
  maturityLabel: string;
}

export interface GapItem {
  id:             string;
  domain:         string;
  title:          string;
  description:    string;
  priority:       "high" | "medium" | "low";
  recommendation: string;
}

// ─── Maturity helpers ─────────────────────────────────────────────────────────

export function calculateMaturityLevel(score: number): {
  level: string;
  description: string;
  colorClass: string;
} {
  if (score < 20) return { level: "Ad-hoc",    colorClass: "destructive", description: "Minimal processes, reactive approach, significant gaps" };
  if (score < 40) return { level: "Emerging",  colorClass: "warning",     description: "Basic processes in place, some standardization" };
  if (score < 60) return { level: "Defined",   colorClass: "blue",        description: "Standardized processes, documented, consistent execution" };
  if (score < 80) return { level: "Advanced",  colorClass: "success",     description: "Optimized processes, data-driven, proactive management" };
  return             { level: "Leading",   colorClass: "success",     description: "Best-in-class, continuous innovation, autonomous capabilities" };
}

// ─── AI Analysis response (returned by NEXT_PUBLIC_DIAGNOSIS_API) ─────────────

export interface AnalysisCategory {
  category:                   string;
  analysis:                   string;
  maturity_level:             "Basic" | "Foundation" | "Intermediate" | "Advanced" | "Best-in-class";
  maturity_transition_logic:  string;
  risk_flags:                 string[];
  confidence_score:           number;
  scmdojo_recommendations:    AnalysisRecommendation[];
}

export interface AnalysisRecommendation {
  title: string;
  type:  string;
  link:  string;
}

export interface AnalysisResponse {
  overall: {
    action_plan:   string;
    maturity_level: "Basic" | "Foundation" | "Intermediate" | "Advanced" | "Best-in-class";
  };
  categories: AnalysisCategory[];
}
